import React, { useState } from "react";
import { Snowflake, Wind, Sun } from "lucide-react";
import { Button, FieldHelp, Input, Field } from "./ui-kit";

// Fonti normative verificate, mostrate come citazione consultabile accanto a
// ogni opzione (icona "?" già usata altrove nell'app per gli aiuti contestuali).
const CITATIONS = {
  refrigerato_generale: "Reg. (CE) 852/2004, Allegato II, Capitolo IX: gli alimenti deperibili vanno mantenuti a temperature che non comportino rischi per la salute, senza interrompere la catena del freddo. Nessuna soglia numerica fissa: la prassi consolidata è 0/+4 °C.",
  refrigerato_fresco: "Reg. (CE) 853/2004, Allegato III — valori di riferimento di settore per prodotti freschi di origine animale: Sezione I (carni) carcasse/tagli ≤ +7 °C, frattaglie ≤ +3 °C; Sezione VIII (prodotti della pesca) temperatura prossima al ghiaccio fondente. Qui applicati in modo cautelativo come buona prassi per preparazioni fresche di carne o pesce.",
  congelato: "Per i prodotti congelati non esiste una soglia di legge dedicata: si adotta come riferimento di buona prassi il valore di -18 °C, fissato per i surgelati dalla Direttiva 89/108/CEE (D.Lgs. 27 gennaio 1992, n. 110). Il valore applicato va indicato nel piano HACCP aziendale.",
  ambiente: "Reg. (CE) 852/2004, Allegato II, Capitolo IX: principio generale di conservazione senza rischi per la salute, al riparo da fonti di calore e luce diretta.",
};

const REGIMES = [
  { key: "refrigerato", label: "Refrigerato (frigorifero)", Icon: Snowflake },
  { key: "congelato", label: "Congelato / Surgelato", Icon: Wind },
  { key: "ambiente", label: "Temperatura ambiente", Icon: Sun },
];
// L'abbattimento non compare qui: e' una fase del processo produttivo (vedi
// "gestione dopo la cottura" nella ricetta), non la modalita' di conservazione.

const FRESH_CATEGORIES = ["Preparazioni di macelleria", "Preparazioni di pescheria"];

const fmtTemp = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return "";
  if (num === 0) return "0 °C";
  return `${num > 0 ? "+" : ""}${num} °C`;
};

// Formulazione standard per il frigorifero, es. "(0/+4 °C)".
export const refrigeratedText = (min, max) => `CONSERVARE IN FRIGORIFERO A TEMPERATURA CONTROLLATA (${fmtTemp(min).replace(" °C", "")}/${fmtTemp(max)})`;

function RegimeFields({ regime, min, setMin, max, setMax, fresh, setFresh }) {
  if (regime === "refrigerato") {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => { setFresh(false); setMin(0); setMax(4); }}
            className={`rounded-lg border-2 px-3 py-2 text-left text-xs ${!fresh ? "border-navy bg-navy text-white" : "border-hairline bg-white text-navy"}`}>
            Refrigerazione generale
          </button>
          <button type="button" onClick={() => { setFresh(true); setMin(0); setMax(2); }}
            className={`rounded-lg border-2 px-3 py-2 text-left text-xs ${fresh ? "border-navy bg-navy text-white" : "border-hairline bg-white text-navy"}`}>
            Prodotto fresco (carne/pesce)
          </button>
          <FieldHelp text={fresh ? CITATIONS.refrigerato_fresco : CITATIONS.refrigerato_generale} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Da (°C)"><Input type="number" value={min} onChange={(e) => setMin(e.target.value)} /></Field>
          <Field label="A (°C)"><Input type="number" value={max} onChange={(e) => setMax(e.target.value)} /></Field>
        </div>
        <p className="rounded-lg bg-white p-2.5 text-xs font-mono text-navy ring-1 ring-hairline">{refrigeratedText(min, max)}</p>
      </div>
    );
  }
  if (regime === "congelato") {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Soglia massima consentita</span>
          <FieldHelp text={CITATIONS.congelato} />
        </div>
        <Field label="Temperatura massima (°C)"><Input type="number" value={max} onChange={(e) => setMax(e.target.value)} className="w-32" /></Field>
        <p className="rounded-lg bg-white p-2.5 text-xs font-mono text-navy ring-1 ring-hairline">CONSERVARE CONGELATO A {fmtTemp(max)} O TEMPERATURA INFERIORE</p>
      </div>
    );
  }
  if (regime === "ambiente") {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Soglia massima consigliata</span>
          <FieldHelp text={CITATIONS.ambiente} />
        </div>
        <Field label="Temperatura massima (°C)"><Input type="number" value={max} onChange={(e) => setMax(e.target.value)} className="w-32" /></Field>
        <p className="rounded-lg bg-white p-2.5 text-xs font-mono text-navy ring-1 ring-hairline">CONSERVARE A TEMPERATURA AMBIENTE, NON SUPERIORE A {fmtTemp(max)}, AL RIPARO DA FONTI DI CALORE E LUCE DIRETTA</p>
      </div>
    );
  }
  return null;
}

function buildText(regime, min, max) {
  if (regime === "refrigerato") return refrigeratedText(min, max);
  if (regime === "congelato") return `CONSERVARE CONGELATO A ${fmtTemp(max)} O TEMPERATURA INFERIORE`;
  if (regime === "ambiente") return `CONSERVARE A TEMPERATURA AMBIENTE, NON SUPERIORE A ${fmtTemp(max)}, AL RIPARO DA FONTI DI CALORE E LUCE DIRETTA`;
  return "";
}

export default function ConservationWizard({ category, onApply }) {
  const [regime, setRegime] = useState(null);
  const [fresh, setFresh] = useState(FRESH_CATEGORIES.includes(category));
  const [min, setMin] = useState(0);
  const [max, setMax] = useState(fresh ? 2 : 4);

  const selectRegime = (key) => {
    setRegime(key);
    if (key === "congelato") setMax(-18);
    else if (key === "ambiente") setMax(25);
    else if (key === "refrigerato") { setMin(0); setMax(fresh ? 2 : 4); }
  };

  const text = regime ? buildText(regime, min, max) : "";

  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-4 space-y-3">
      <div className="text-sm font-semibold text-navy">Come va conservato il prodotto?</div>
      <div className="grid gap-2 sm:grid-cols-2">
        {REGIMES.map((r) => (
          <button key={r.key} type="button" onClick={() => selectRegime(r.key)}
            className={`flex items-center gap-2 rounded-lg border-2 px-3 py-2.5 text-left text-sm transition-colors ${regime === r.key ? "border-indigo-500 bg-indigo-100 font-medium text-indigo-900" : "border-hairline bg-white text-navy hover:border-indigo-300"}`}>
            <r.Icon className="h-4 w-4 shrink-0" /> {r.label}
          </button>
        ))}
      </div>

      {regime && (
        <RegimeFields regime={regime} min={min} setMin={setMin} max={max} setMax={setMax} fresh={fresh} setFresh={setFresh} />
      )}

      {text && (
        <Button variant="success" className="w-full" onClick={() => onApply(text)}>Usa questa dicitura</Button>
      )}
    </div>
  );
}
